package com.yendo.mx.ui.home

import androidx.compose.foundation.background
import androidx.compose.foundation.clickable
import androidx.compose.foundation.layout.*
import androidx.compose.foundation.lazy.LazyColumn
import androidx.compose.foundation.lazy.items
import androidx.compose.foundation.shape.CircleShape
import androidx.compose.foundation.shape.RoundedCornerShape
import androidx.compose.material.icons.Icons
import androidx.compose.material.icons.filled.*
import androidx.compose.material3.*
import androidx.compose.runtime.*
import androidx.compose.ui.Alignment
import androidx.compose.ui.Modifier
import androidx.compose.ui.draw.clip
import androidx.compose.ui.graphics.Color
import androidx.compose.ui.layout.ContentScale
import androidx.compose.ui.platform.UriHandler
import androidx.compose.ui.text.font.FontWeight
import androidx.compose.ui.unit.dp
import androidx.compose.ui.unit.sp
import androidx.compose.ui.window.Dialog
import androidx.compose.ui.window.DialogProperties
import coil3.compose.AsyncImage
import coil3.compose.LocalPlatformContext
import coil3.request.ImageRequest
import coil3.request.crossfade
import com.yendo.mx.models.PedidoFirebase
import com.yendo.mx.models.Producto
import com.yendo.mx.models.RestauranteFirebase
import com.yendo.mx.models.PromoFlashConfig
import com.yendo.mx.utils.getAhora
import com.yendo.mx.utils.AdAnalyticsManager
import com.yendo.mx.ui.theme.RosaYendo
import dev.gitlive.firebase.Firebase
import dev.gitlive.firebase.firestore.firestore
import kotlinx.coroutines.CoroutineScope
import kotlinx.coroutines.launch

@Composable
fun PromoFlashDialog(
    promoConfig: PromoFlashConfig,
    uriHandler: UriHandler,
    coroutineScope: CoroutineScope,
    onDismiss: () -> Unit,
    procesandoAccion: (Boolean) -> Unit,
    onRestauranteClick: (RestauranteFirebase) -> Unit,
    onProductoDirectoClick: (Producto, RestauranteFirebase) -> Unit
) {
    // 🚀 ANALÍTICA: Registrar impresión de la Promo Flash
    LaunchedEffect(promoConfig.destino_accion) {
        AdAnalyticsManager.logAdView(
            adId = promoConfig.destino_accion.ifBlank { "ZONA_PROMO" },
            adType = "PROMO_FLASH"
        )
    }

    Dialog(
        onDismissRequest = onDismiss,
        properties = DialogProperties(usePlatformDefaultWidth = false)
    ) {
        Box(
            modifier = Modifier
                .fillMaxWidth(0.85f)
                .aspectRatio(0.75f)
                .clip(RoundedCornerShape(20.dp))
                .background(Color.Black)
        ) {
            AsyncImage(
                model = ImageRequest.Builder(LocalPlatformContext.current)
                    .data(promoConfig.imagen)
                    .crossfade(true)
                    .build(),
                contentDescription = "Promoción de la Zona",
                contentScale = ContentScale.Crop,
                modifier = Modifier
                    .fillMaxSize()
                    .clickable {
                        val tipo = promoConfig.tipo_accion.uppercase()
                        val destino = promoConfig.destino_accion

                        // 🚀 ANALÍTICA: Registrar click en la Promo Flash
                        AdAnalyticsManager.logAdClick(
                            adId = destino.ifBlank { "ZONA_PROMO" },
                            adType = "PROMO_FLASH"
                        )

                        if (destino.isNotBlank()) {
                            when (tipo) {
                                "NEGOCIO" -> {
                                    coroutineScope.launch {
                                        procesandoAccion(true)
                                        try {
                                            val doc = Firebase.firestore
                                                .collection("Negocios").document(destino).get()
                                            val rest = doc.data<RestauranteFirebase>().copy(id = doc.id)
                                            
                                            val ahora = getAhora()
                                            val estaPausado = rest.operacion.pausadoManualmente && rest.operacion.pausaHasta > ahora
                                            val esVisible = rest.operacion.visible && rest.operacion.activoComercio && !rest.operacion.bloqueadoPorAdmin && !estaPausado

                                            if (esVisible) {
                                                onRestauranteClick(rest)
                                            } else {
                                                println("⚠️ Negocio no disponible desde promo")
                                            }
                                        } catch (e: Exception) {
                                            println("🚨 Error abriendo negocio desde promo: ${e.message}")
                                        } finally {
                                            procesandoAccion(false)
                                        }
                                    }
                                }
                                "PRODUCTO" -> {
                                    coroutineScope.launch {
                                        procesandoAccion(true)
                                        try {
                                            val prodDoc = Firebase.firestore
                                                .collection("Productos").document(destino).get()
                                            val prod = prodDoc.data<Producto>().copy(id = prodDoc.id)
                                            
                                            val restDoc = Firebase.firestore
                                                .collection("Negocios").document(prod.idRestaurante).get()
                                            val rest = restDoc.data<RestauranteFirebase>().copy(id = restDoc.id)

                                            val ahora = getAhora()
                                            val estaPausado = rest.operacion.pausadoManualmente && rest.operacion.pausaHasta > ahora
                                            val esVisible = rest.operacion.visible && rest.operacion.activoComercio && !rest.operacion.bloqueadoPorAdmin && !estaPausado

                                            if (esVisible) {
                                                onProductoDirectoClick(prod, rest)
                                                // 🚀 ANALÍTICA: Registro de vista desde Promo Flash
                                                AdAnalyticsManager.logItemView(
                                                    productId = prod.id,
                                                    productName = prod.nombre,
                                                    price = prod.precio,
                                                    merchantName = rest.infoPublica.nombre
                                                )
                                            } else {
                                                println("⚠️ Producto no disponible por jerarquía de negocio")
                                            }
                                        } catch (e: Exception) {
                                            println("🚨 Error abriendo producto desde promo: ${e.message}")
                                        } finally {
                                            procesandoAccion(false)
                                        }
                                    }
                                }
                                "WEB" -> {
                                    try {
                                        val urlSegura = if (!destino.startsWith("http")) "https://$destino" else destino
                                        uriHandler.openUri(urlSegura)
                                    } catch (e: Exception) {
                                        println("🚨 Error abriendo enlace web desde promo")
                                    }
                                }
                            }
                        }
                        onDismiss()
                    }
            )

            IconButton(
                onClick = onDismiss,
                modifier = Modifier
                    .align(Alignment.TopEnd)
                    .padding(8.dp)
                    .size(36.dp)
                    .background(Color.Black.copy(alpha = 0.5f), CircleShape)
            ) {
                Icon(Icons.Default.Close, "Cerrar", tint = Color.White, modifier = Modifier.size(20.dp))
            }
        }
    }
}

@Composable
fun DialogoBienvenidaYendo(
    colorTextoPrincipal: Color,
    colorTextoSecundario: Color,
    colorFondoTarjetas: Color,
    onDismiss: () -> Unit,
    onElegirUbicacion: () -> Unit
) {
    AlertDialog(
        onDismissRequest = onDismiss,
        title = { Text("¡Bienvenido a Yendo Mx!", fontWeight = FontWeight.Bold, color = colorTextoPrincipal) },
        text = { Text("Para mostrarte los mejores restaurantes cerca de ti y calcular tu tiempo de entrega, por favor indicanos a dónde llevaremos tu pedido.", color = colorTextoSecundario) },
        confirmButton = { 
            Button(
                onClick = onElegirUbicacion, 
                colors = ButtonDefaults.buttonColors(RosaYendo)
            ) { 
                Text("Elegir ubicación", fontWeight = FontWeight.Bold, color = Color.White) 
            } 
        },
        containerColor = colorFondoTarjetas, 
        shape = RoundedCornerShape(16.dp)
    )
}

@Composable
fun DialogoInvitadoYendo(
    colorTextoPrincipal: Color,
    colorTextoSecundario: Color,
    colorFondoTarjetas: Color,
    onDismiss: () -> Unit,
    onRegistrarse: () -> Unit
) {
    AlertDialog(
        onDismissRequest = onDismiss,
        title = { Text("¡Únete a Yendo Mx!", fontWeight = FontWeight.Bold, color = colorTextoPrincipal) },
        text = { Text("Crea una cuenta para guardar tus direcciones y realizar pedidos de forma segura.", color = colorTextoSecundario) },
        confirmButton = { 
            Button(
                onClick = onRegistrarse, 
                colors = ButtonDefaults.buttonColors(RosaYendo)
            ) { 
                Text("Registrarme", color = Color.White) 
            } 
        },
        dismissButton = { 
            TextButton(onClick = onDismiss) { 
                Text("Más tarde", color = colorTextoSecundario) 
            } 
        },
        containerColor = colorFondoTarjetas
    )
}

@OptIn(ExperimentalMaterial3Api::class)
@Composable
fun SheetMultiplesPedidosYendo(
    pedidosActivos: List<PedidoFirebase>,
    colorTextoPrincipal: Color,
    colorTextoSecundario: Color,
    colorFondoTarjetas: Color,
    colorFondoTarjetasSecundario: Color,
    onDismiss: () -> Unit,
    onPedidoSeleccionado: (PedidoFirebase) -> Unit
) {
    ModalBottomSheet(
        onDismissRequest = onDismiss, 
        containerColor = colorFondoTarjetas
    ) {
        Column(modifier = Modifier.fillMaxWidth().padding(horizontal = 24.dp, vertical = 8.dp)) {
            Text("Tus pedidos en curso", fontWeight = FontWeight.ExtraBold, fontSize = 20.sp, color = colorTextoPrincipal)
            Text("Selecciona el pedido que deseas rastrear", color = colorTextoSecundario, fontSize = 14.sp)
            Spacer(modifier = Modifier.height(20.dp))
            LazyColumn(modifier = Modifier.fillMaxWidth(), verticalArrangement = Arrangement.spacedBy(12.dp)) {
                items(pedidosActivos) { ped ->
                    Card(
                        onClick = { onPedidoSeleccionado(ped) },
                        modifier = Modifier.fillMaxWidth(), 
                        colors = CardDefaults.cardColors(containerColor = colorFondoTarjetasSecundario), 
                        shape = RoundedCornerShape(16.dp)
                    ) {
                        Row(modifier = Modifier.fillMaxWidth().padding(16.dp), verticalAlignment = Alignment.CenterVertically) {
                            Box(modifier = Modifier.size(40.dp).background(RosaYendo.copy(alpha = 0.1f), CircleShape), contentAlignment = Alignment.Center) {
                                Icon(if (ped.tipoPedido.uppercase() == "PICKUP") Icons.Default.ShoppingBag else Icons.Default.TwoWheeler, null, tint = RosaYendo)
                            }
                            Spacer(modifier = Modifier.width(16.dp))
                            Column(modifier = Modifier.weight(1f)) {
                                Text(ped.nombreRestaurante, fontWeight = FontWeight.Bold, fontSize = 16.sp, color = colorTextoPrincipal)
                                Text("Orden #${ped.idPedido.takeLast(6).uppercase()} • ${com.yendo.mx.utils.formatEstadoHuman(ped.estadoActual)}", color = colorTextoSecundario, fontSize = 12.sp)
                            }
                            Icon(Icons.Default.ChevronRight, null, tint = colorTextoSecundario)
                        }
                    }
                }
            }
            Spacer(modifier = Modifier.height(40.dp).navigationBarsPadding())
        }
    }
}
